// ---------------------------------------------------------------------------
// Entradas de tiempo por día (#783). Lógica pura de presentación: se prueba sin
// montar componentes (ni tocar `utils/api` → Firebase).
//
// Regla que se pinta: un día-persona con entradas vigentes cuenta sus minutos;
// uno sin entradas muestra el estimado por bloques de tiempo real (#960),
// rotulado «estimado».
// ---------------------------------------------------------------------------
import {
  addDays,
  approvalStatusOf,
  formatMinutes,
  hasFlags,
  isCountedEntry,
  isPendingEntry,
  mergeFlags,
  weekStartOf,
  type EstimateBlock,
  type ResolvedDay,
  type TimeEntryApprovalStatus,
  type TimeEntryEvidence,
  type TimeEntryFlag,
} from "@torre/work-hours";
import type { TimeEntryRow } from "../services/workHoursService";
import { GITHUB_BASE, pullRequestUrl, repoUrl, safeHttpUrl } from "./github";

export { formatMinutes };

export interface DayGroup {
  key: string;
  date: string;
  userId: string;
  userName: string | null;
  /** Sin entradas vigentes: las horas son el estimado por bloques de tiempo real (#960). */
  estimated: boolean;
  minutes: number;
  hours: number;
  /** Evidencia del día estimado. */
  bucketCount: number;
  commitCount: number;
  /** #960: bloques de reloj que dieron los minutos de un día estimado. */
  blocks: EstimateBlock[];
  /** Todas las entradas del día (también las por aprobar y las rechazadas, #1281): la lista las pinta con su estado. */
  entries: TimeEntryRow[];
  /** #843: parte de `minutes` fuera de asignación (cuenta igual) y sus motivos. */
  outOfAllocationMinutes: number;
  flags: TimeEntryFlag[];
  /** #1281: minutos que esperan al tech lead. NO están en `minutes`. */
  pendingMinutes: number;
}

/** ¿La entrada está marcada fuera de asignación (#843)? */
export const isFlaggedEntry = (e: Pick<TimeEntryRow, "flags">): boolean => hasFlags(e);

/** Estado de aprobación de una entrada para pintarlo (#1281); `null` si no la necesitó. */
export const entryApprovalStatus = (e: Pick<TimeEntryRow, "approval">): TimeEntryApprovalStatus | null => approvalStatusOf(e);

/**
 * Texto de una marca para la pantalla: el motivo del sistema y, aparte, la
 * justificación de la persona (`null` ⇒ «sin justificación», p. ej. franjas).
 */
export function flagLines(flags: TimeEntryFlag[] | null | undefined): { message: string; reason: string | null }[] {
  return (flags ?? []).map((f) => ({ message: f.message, reason: f.reason ? String(f.reason) : null }));
}

/**
 * Junta los días que resolvió el backend (`summary.days`, con su marca de
 * estimado) con las entradas vigentes. Un día con entradas que no venga en
 * `days` (p. ej. backend anterior a #783) se arma solo con sus entradas.
 * Orden: el día más reciente primero y, dentro del día, por persona.
 */
export function buildDayGroups(
  days: ResolvedDay[] | null | undefined,
  entries: TimeEntryRow[],
  names: Record<string, string> = {},
): DayGroup[] {
  const groups = new Map<string, DayGroup>();
  for (const d of days ?? []) {
    const key = `${d.userId}|${d.date}`;
    groups.set(key, {
      key,
      date: d.date,
      userId: d.userId,
      userName: d.userName ?? names[d.userId] ?? null,
      estimated: d.estimated,
      minutes: d.minutes,
      hours: d.hours,
      bucketCount: d.bucketCount,
      commitCount: d.commitCount,
      blocks: d.estimated ? d.blocks ?? [] : [],
      entries: [],
      outOfAllocationMinutes: d.outOfAllocationMinutes ?? 0,
      flags: d.flags ?? [],
      pendingMinutes: 0,
    });
  }
  for (const e of entries) {
    if (e.voided) continue;
    const key = `${e.userId}|${e.date}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        date: e.date,
        userId: e.userId,
        userName: names[e.userId] ?? null,
        estimated: false,
        minutes: 0,
        hours: 0,
        bucketCount: 0,
        commitCount: 0,
        blocks: [],
        entries: [],
        outOfAllocationMinutes: 0,
        flags: [],
        pendingMinutes: 0,
      };
      groups.set(key, g);
    }
    g.entries.push(e);
  }
  for (const g of groups.values()) {
    // #1281: las que esperan al tech lead se suman aparte; las rechazadas sólo se listan.
    g.pendingMinutes = g.entries.filter(isPendingEntry).reduce((n, e) => n + (Number(e.minutes) || 0), 0);
    const counted = g.entries.filter(isCountedEntry);
    if (counted.length) {
      // Con entradas que cuentan manda la suma de sus minutos (aunque el resumen dijera otra cosa).
      g.estimated = false;
      g.blocks = [];
      g.minutes = counted.reduce((n, e) => n + (Number(e.minutes) || 0), 0);
      g.hours = Math.round((g.minutes / 60) * 100) / 100;
      // #843: lo fuera de asignación sale de las entradas marcadas (cuentan igual).
      const flagged = counted.filter(isFlaggedEntry);
      g.outOfAllocationMinutes = flagged.reduce((n, e) => n + (Number(e.minutes) || 0), 0);
      g.flags = mergeFlags(...flagged.map((e) => e.flags));
    }
    // Un día con SOLO entradas por aprobar o rechazadas conserva lo que dijo el
    // resumen (estimado por commits, o nada): igual que en el servidor.
  }
  return [...groups.values()]
    .filter((g) => g.minutes > 0 || g.pendingMinutes > 0 || g.entries.length > 0)
    .sort((a, b) =>
      a.date === b.date ? String(a.userName ?? a.userId).localeCompare(String(b.userName ?? b.userId), "es") : a.date < b.date ? 1 : -1,
    );
}

export interface EvidenceLink {
  kind: "commit" | "pr" | "issue" | "artifact";
  label: string;
  url: string | null;
}

const REF = /^([A-Za-z0-9._-]+\/[A-Za-z0-9._-]+)#(\d+)$/;
const COMMIT_REF = /^([A-Za-z0-9._-]+\/[A-Za-z0-9._-]+)@([0-9a-f]{7,40})$/i;
const SHA = /^[0-9a-f]{7,40}$/i;
const shortRepo = (repo: string) => repo.split("/")[1] ?? repo;

/**
 * Enlaces de la evidencia de una entrada. Formatos que entiende:
 *   • commits: `owner/repo@sha`, una URL o un SHA suelto (sin enlace: no se sabe el repo);
 *   • prs / issues: `owner/repo#123` o una URL;
 *   • artifacts: una URL (o texto).
 * Nada se enlaza a ciegas: solo http(s) o rutas de GitHub derivadas de `owner/repo`.
 */
export function evidenceLinks(evidence: TimeEntryEvidence | null | undefined): EvidenceLink[] {
  if (!evidence) return [];
  const out: EvidenceLink[] = [];
  for (const raw of evidence.commits ?? []) {
    const url = safeHttpUrl(raw);
    const m = COMMIT_REF.exec(raw);
    if (url) out.push({ kind: "commit", label: raw.split("/").pop()?.slice(0, 7) || raw, url });
    else if (m) out.push({ kind: "commit", label: `${shortRepo(m[1])}@${m[2].slice(0, 7)}`, url: repoUrl(m[1]) ? `${repoUrl(m[1])}/commit/${m[2]}` : null });
    else out.push({ kind: "commit", label: SHA.test(raw) ? raw.slice(0, 7) : raw, url: null });
  }
  for (const [kind, list] of [
    ["pr", evidence.prs ?? []],
    ["issue", evidence.issues ?? []],
  ] as const) {
    for (const raw of list) {
      const url = safeHttpUrl(raw);
      const m = REF.exec(raw);
      if (url) out.push({ kind, label: raw.replace(/^https?:\/\/(www\.)?github\.com\//i, ""), url });
      else if (m) {
        const number = Number(m[2]);
        const link = kind === "pr" ? pullRequestUrl({ repo: m[1], number }) : repoUrl(m[1]) ? `${repoUrl(m[1])}/issues/${number}` : null;
        out.push({ kind, label: `${shortRepo(m[1])}#${number}`, url: link });
      } else out.push({ kind, label: raw, url: null });
    }
  }
  for (const raw of evidence.artifacts ?? []) out.push({ kind: "artifact", label: raw.replace(/^https?:\/\//i, "").slice(0, 60), url: safeHttpUrl(raw) });
  return out;
}

/** ¿El enlace apunta a GitHub? (para el ícono). */
export const isGithubLink = (url: string | null) => Boolean(url && url.startsWith(GITHUB_BASE));

// ---------------------------------------------------------------------------
// Vista por semana (#1061): los días del rango agrupados de lunes a domingo.
// ---------------------------------------------------------------------------

export interface WeekGroup {
  /** Lunes de la semana (`YYYY-MM-DD`). */
  weekStart: string;
  /** Domingo de la semana. */
  weekEnd: string;
  /** Minutos registrados (entradas) de la semana. */
  recordedMinutes: number;
  /** Horas estimadas por commits en días sin entradas. */
  estimatedHours: number;
  /** Los días de la semana con horas, en orden. */
  days: DayGroup[];
}

/** Días → semanas (lunes a domingo), de la más antigua a la más reciente. */
export function buildWeekGroups(days: DayGroup[]): WeekGroup[] {
  const byWeek = new Map<string, WeekGroup>();
  for (const d of days) {
    const weekStart = weekStartOf(d.date);
    const w = byWeek.get(weekStart) ?? { weekStart, weekEnd: addDays(weekStart, 6), recordedMinutes: 0, estimatedHours: 0, days: [] };
    if (d.estimated) w.estimatedHours = Math.round((w.estimatedHours + d.hours) * 100) / 100;
    else w.recordedMinutes += d.minutes;
    w.days.push(d);
    byWeek.set(weekStart, w);
  }
  return [...byWeek.values()].sort((a, b) => a.weekStart.localeCompare(b.weekStart));
}
